import React, { Suspense, useEffect, useState } from "react";
import { graphql, useFragment, useLazyLoadQuery } from "react-relay";
import { useIntl } from "react-intl";
import {
  AHIcon,
  AHSeparator,
  Box,
  Dialog,
  ListItem,
  SectionHeader,
} from "arkh";
import { Keyboard } from "react-native";
import { FlashList } from "@shopify/flash-list";

import { RoutineEditAssignedRolesListItem_Routine$key } from "./__generated__/RoutineEditAssignedRolesListItem_Routine.graphql";
import { RoutineEditAssignedRolesListItem_Dialog_roles$key } from "./__generated__/RoutineEditAssignedRolesListItem_Dialog_roles.graphql";
import { RoutineEditAssignedRolesListItemQuery } from "./__generated__/RoutineEditAssignedRolesListItemQuery.graphql";
import { RoleListItem } from "widgets/src/RoleListItem/RoleListItem";
import {
  useRoutineEditState,
  useRoutineEditStateSetParams,
} from "../State/RoutineEditState";
import { onPressInHaptic } from "widgets/src/onPressInHaptic";
import { useEvent } from "widgets/src/useEvent";
import { useIntlTokens } from "../../../components/mobile/useIntlTokens";

export function RoutineEditAssignedRolesListItem(props: {
  routine: RoutineEditAssignedRolesListItem_Routine$key;
}) {
  const routine = useFragment(
    graphql`
      fragment RoutineEditAssignedRolesListItem_Routine on Routine {
        isOrg
        ownerTeam {
          id
        }
      }
    `,
    props.routine
  );

  const params = useRoutineEditState();
  const teamId = params.team?.id ?? routine.ownerTeam?.id;

  // Don't show for org/workspace routines or if no team
  if (routine.isOrg || !teamId) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <RoutineEditAssignedRolesListItemContent teamId={teamId} />
    </Suspense>
  );
}

function RoutineEditAssignedRolesListItemContent(props: { teamId: string }) {
  const intl = useIntl();
  const intl_tokens = useIntlTokens();
  const [dialogVisible, setDialogVisible] = useState(false);

  const data = useLazyLoadQuery<RoutineEditAssignedRolesListItemQuery>(
    graphql`
      query RoutineEditAssignedRolesListItemQuery($teamId: ID!) {
        node(id: $teamId) {
          ... on Team {
            roles {
              id
              name
              ...RoutineEditAssignedRolesListItem_Dialog_roles
            }
          }
        }
      }
    `,
    { teamId: props.teamId }
  );

  const assignableRoles = data.node?.roles ?? [];

  const params = useRoutineEditState();
  const setParams = useRoutineEditStateSetParams();

  const assignedRoleIds = params.assignedRoleIds ?? [];

  const selectedRoleNames = (() => {
    const names = assignableRoles
      .filter((role) => assignedRoleIds.includes(role.id))
      .map((role) => role.name)
      .filter((name): name is string => !!name);
    return names.length > 0 ? names.join(", ") : null;
  })();

  const setAssignedRoleIds = (roleIds: string[]) => {
    setParams({ assignedRoleIds: roleIds });
  };

  if (assignableRoles.length === 0) {
    return null;
  }

  return (
    <>
      <SectionHeader>
        <SectionHeader.Title>{intl_tokens.Role}</SectionHeader.Title>
      </SectionHeader>
      <ListItem
        isFree
        border="border-primary"
        right={<ListItem.NavArrow />}
        left={
          <AHIcon type="employee-role" size="small" color="text-secondary" />
        }
        onPress={() => {
          Keyboard.dismiss();
          setDialogVisible(true);
        }}
      >
        <ListItem.Title>
          {selectedRoleNames ??
            intl.formatMessage({
              defaultMessage: "None",
              description: "RoutineEditAssignedRolesListItem no roles selected",
            })}
        </ListItem.Title>
      </ListItem>
      <RoutineEditAssignedRolesDialog
        visible={dialogVisible}
        onClose={() => setDialogVisible(false)}
        assignableRoles={assignableRoles}
        assignedRoleIds={assignedRoleIds}
        setAssignedRoleIds={setAssignedRoleIds}
      />
    </>
  );
}

function RoutineEditAssignedRolesDialog(props: {
  visible: boolean;
  onClose: () => void;
  assignableRoles: RoutineEditAssignedRolesListItem_Dialog_roles$key;
  assignedRoleIds: string[];
  setAssignedRoleIds: (roleIds: string[]) => void;
}) {
  const intl = useIntl();

  const roles = useFragment(
    graphql`
      fragment RoutineEditAssignedRolesListItem_Dialog_roles on EmployeeRole
      @relay(plural: true) {
        id
        name
        ...RoleListItem_role
      }
    `,
    props.assignableRoles
  );

  const [initialAssignedRoleIds, setInitialAssignedRoleIds] = useState(
    props.assignedRoleIds
  );

  useEffect(() => {
    if (props.visible) {
      setInitialAssignedRoleIds(props.assignedRoleIds);
    }
  }, [props.visible]);

  const sortedRoles = [...roles].sort((a, b) => {
    const aSelected = initialAssignedRoleIds.includes(a.id) ? 1 : 0;
    const bSelected = initialAssignedRoleIds.includes(b.id) ? 1 : 0;
    return bSelected - aSelected;
  });

  const onPressRole = useEvent((id: string) => {
    onPressInHaptic();
    const { assignedRoleIds, setAssignedRoleIds } = props;
    setAssignedRoleIds(
      assignedRoleIds.includes(id)
        ? assignedRoleIds.filter((existingId) => existingId !== id)
        : [...assignedRoleIds, id]
    );
  });

  const renderItem = ({ item }: { item: (typeof sortedRoles)[number] }) => {
    const selected = props.assignedRoleIds.includes(item.id);
    return (
      <RoleListItem role={item} selected={selected} onPress={onPressRole} />
    );
  };

  const title = intl.formatMessage({
    defaultMessage: "Assigned roles",
    description: "RoutineEditAssignedRolesDialog title",
  });

  return (
    <Dialog
      visible={props.visible}
      onClose={props.onClose}
      height={"60%"}
      maxHeight={400}
      title={title}
    >
      <AHSeparator />
      <Box style={{ flex: 1 }}>
        <FlashList
          data={sortedRoles}
          renderItem={renderItem}
          keyExtractor={(item) => item.id}
          ItemSeparatorComponent={AHSeparator}
        />
      </Box>
    </Dialog>
  );
}
